import {
  MessageEnvelope,
  MessageListBucketMeta,
  MessageListItem,
  MessageListMessage,
} from '../../schema/message';
import { createQuerySeparator, createDateSeparator } from './create-separator';
import { getMessageBucket, QueryGroupRange } from './get-message-bucket';
import { getMessageDayKey } from './message-date-time';

export const buildGroupedMessages = (
  visibleMessages: MessageEnvelope[],
  growGroupRanges: QueryGroupRange[]
): MessageListItem[] => {
  const groupedMessages: MessageListItem[] = [];
  const growRangeIndices = new Map<
    string,
    {
      firstIndex: number;
      lastIndex: number;
    }
  >();

  for (let index = 0; index < visibleMessages.length; index++) {
    const bucket = getMessageBucket(visibleMessages[index], growGroupRanges);
    if (bucket.type !== 'query') {
      continue;
    }

    const existingRange = growRangeIndices.get(bucket.key);
    if (existingRange) {
      existingRange.lastIndex = index;
      continue;
    }

    growRangeIndices.set(bucket.key, {
      firstIndex: index,
      lastIndex: index,
    });
  }

  for (let index = visibleMessages.length - 1; index >= 0; index--) {
    const message = visibleMessages[index];
    const bucket = getMessageBucket(message, growGroupRanges);
    const newerMessage =
      index < visibleMessages.length - 1
        ? visibleMessages[index + 1]
        : undefined;
    const olderMessage = index > 0 ? visibleMessages[index - 1] : undefined;
    const newerDayKey = newerMessage
      ? getMessageDayKey(newerMessage)
      : undefined;
    const currentDayKey = getMessageDayKey(message);
    const olderDayKey = olderMessage
      ? getMessageDayKey(olderMessage)
      : undefined;
    const growRange =
      bucket.type === 'query' ? growRangeIndices.get(bucket.key) : undefined;

    const messageBoundaryState = {
      isFirstGrowMessageInRange:
        bucket.type === 'query' && growRange?.firstIndex === index,
      isLastGrowMessageInRange:
        bucket.type === 'query' && growRange?.lastIndex === index,
      hasDayChangedFromOlder: !olderMessage || currentDayKey !== olderDayKey,
      hasDayChangedFromNewer: !newerMessage || currentDayKey !== newerDayKey,
    };

    if (messageBoundaryState.isLastGrowMessageInRange) {
      groupedMessages.push(
        createQuerySeparator(
          bucket as Extract<MessageListBucketMeta, { type: 'query' }>,
          'end'
        )
      );
    }

    const messageItem: MessageListMessage = {
      ...message,
      messageListBucket: bucket,
    };
    groupedMessages.push(messageItem);

    if (messageBoundaryState.isFirstGrowMessageInRange) {
      groupedMessages.push(
        createQuerySeparator(
          bucket as Extract<MessageListBucketMeta, { type: 'query' }>,
          'start'
        )
      );
    }

    if (messageBoundaryState.hasDayChangedFromOlder) {
      groupedMessages.push(createDateSeparator(currentDayKey));
    }
  }

  return groupedMessages;
};
